import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, Divider, Alert, CircularProgress,
  List, ListItem, ListItemIcon, ListItemText, IconButton, Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  AttachFile as AttachIcon,
  Download as DownloadIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import apiService from '../services/api';

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd/MM/yyyy HH:mm'); } catch { return iso; }
}

function fmtBytes(n: number | null) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

export default function KnowledgeBaseArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canAuthor, setCanAuthor] = useState(false);

  useEffect(() => {
    apiService.getCurrentUser?.().then((u: any) => {
      setCanAuthor(u?.can_author_kb || u?.is_admin || false);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiService.kbGetArticle(id)
      .then(setArticle)
      .catch(() => setError('Failed to load article'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!article) return null;

  const isExpired = article.is_expired;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" gap={1} mb={3}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/knowledge-base')}>Back</Button>
        {canAuthor && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/knowledge-base/${id}/edit`)}>
            Edit Article
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h4" fontWeight={700} sx={{ flex: 1 }}>{article.title}</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={isExpired ? 'Expired' : article.status}
              color={isExpired ? 'error' : STATUS_COLORS[article.status] ?? 'default'}
            />
            {article.is_public && <Chip icon={<PublicIcon />} label="Public" color="info" />}
          </Box>
        </Box>

        {/* Meta */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          {article.category_name && (
            <Typography variant="body2" color="text.secondary">Category: <strong>{article.category_name}</strong></Typography>
          )}
          <Typography variant="body2" color="text.secondary">By: <strong>{article.created_by_name || '—'}</strong></Typography>
          <Typography variant="body2" color="text.secondary">Updated: <strong>{fmtDate(article.updated_at)}</strong></Typography>
          {article.expires_at && (
            <Typography variant="body2" color={isExpired ? 'error' : 'text.secondary'}>
              Expires: <strong>{fmtDate(article.expires_at)}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">Views: <strong>{article.view_count}</strong></Typography>
        </Box>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap" mb={2}>
            {article.tags.map((t: any) => <Chip key={t.id} label={t.name} size="small" variant="outlined" />)}
          </Box>
        )}

        {article.excerpt && (
          <Alert severity="info" sx={{ mb: 3 }}>{article.excerpt}</Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Content — render Quill HTML safely */}
        <Box
          sx={{
            '& h1, & h2, & h3': { mt: 2, mb: 1 },
            '& p': { mb: 1 },
            '& pre': { background: '#1e1e2e', color: '#cdd6f4', p: 2, borderRadius: 1, overflowX: 'auto', fontSize: 13 },
            '& blockquote': { borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, color: 'text.secondary', my: 1 },
            '& img': { maxWidth: '100%', borderRadius: 1 },
            '& iframe': { maxWidth: '100%', borderRadius: 1 },
            '& ul, & ol': { pl: 3 },
            '& a': { color: 'primary.main' },
          }}
          dangerouslySetInnerHTML={{ __html: article.content || '<p><em>No content.</em></p>' }}
        />

        {/* Attachments */}
        {article.attachments?.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>Attachments</Typography>
            <List dense>
              {article.attachments.map((att: any) => (
                <ListItem key={att.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}><AttachIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={att.display_name}
                    secondary={fmtBytes(att.file_size_bytes)}
                  />
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      component="a"
                      href={apiService.kbAttachmentDownloadUrl(article.id, att.id)}
                      download
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Paper>
    </Box>
  );
}
